/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable no-unused-vars */
import React, { useState, useCallback, useRef, useEffect } from 'react';
import ReactFlow, {
  MiniMap,
  Controls,
  Background,
  useNodesState,
  useEdgesState,
  MarkerType,
  Handle,
  Position,
} from 'reactflow';
import 'reactflow/dist/style.css';
import Anthropic from '@anthropic-ai/sdk';

// Editable Node Component
const EditableNode = ({ data }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [value, setValue] = useState(data.displayValue);
  const clickTimerRef = React.useRef(null);

  const handleDoubleClick = (e) => {
    e.stopPropagation();
    // Clear any pending single-click
    if (clickTimerRef.current) {
      clearTimeout(clickTimerRef.current);
      clickTimerRef.current = null;
    }
    setIsEditing(true);
  };

  const handleClick = (e) => {
    e.stopPropagation();
    if (isEditing) return;
    
    // Delay the onClick to see if a double-click happens
    clickTimerRef.current = setTimeout(() => {
      if (data.onClick) {
        data.onClick();
      }
      clickTimerRef.current = null;
    }, 250);
  };

  const handleBlur = () => {
    setIsEditing(false);
    if (data.onValueChange) {
      const numValue = parseFloat(value.replace(/[^0-9.-]/g, ''));
      if (!isNaN(numValue)) {
        data.onValueChange(numValue);
      }
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleBlur();
    }
  };

  return (
    <div
      onDoubleClick={handleDoubleClick}
      onClick={handleClick}
      style={{
        padding: '10px 16px',
        borderRadius: '50%',
        background: data.background,
        border: `3px solid ${data.borderColor}`,
        color: '#1f2937',
        fontWeight: 600,
        fontSize: '12px',
        minWidth: '80px',
        minHeight: '80px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: isEditing ? 'text' : 'pointer',
        boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
        position: 'relative',
      }}
    >
      <Handle type="target" position={Position.Top} id="top" style={{ opacity: 0 }} />
      <Handle type="target" position={Position.Bottom} id="bottom" style={{ opacity: 0 }} />
      <Handle type="target" position={Position.Left} id="left" style={{ opacity: 0 }} />
      <Handle type="target" position={Position.Right} id="right" style={{ opacity: 0 }} />
      <Handle type="source" position={Position.Top} id="top" style={{ opacity: 0 }} />
      <Handle type="source" position={Position.Bottom} id="bottom" style={{ opacity: 0 }} />
      <Handle type="source" position={Position.Left} id="left" style={{ opacity: 0 }} />
      <Handle type="source" position={Position.Right} id="right" style={{ opacity: 0 }} />
      <div style={{ fontSize: '9px', marginBottom: '3px', textAlign: 'center', fontWeight: 500 }}>
        {data.name}
      </div>
      {isEditing ? (
        <input
          type="text"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          autoFocus
          style={{
            width: '60px',
            textAlign: 'center',
            fontSize: '14px',
            fontWeight: 700,
            border: '2px solid #3b82f6',
            borderRadius: '4px',
            padding: '2px',
          }}
        />
      ) : (
        <div style={{ fontSize: '15px', fontWeight: 700 }}>{data.displayValue}</div>
      )}
    </div>
  );
};

// Loop Identifier Component
const LoopIdentifier = ({ data }) => {
  return (
    <div
      onClick={data.onClick}
      style={{
        padding: '6px 12px',
        borderRadius: '12px',
        background: data.background,
        border: `2px solid ${data.borderColor}`,
        color: data.textColor || '#1f2937',
        fontWeight: 700,
        fontSize: '11px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.15)',
        cursor: 'pointer',
        userSelect: 'none',
        whiteSpace: 'nowrap',
      }}
    >
      {data.label}
    </div>
  );
};

const nodeTypes = {
  editable: EditableNode,
  loopIdentifier: LoopIdentifier,
};

// Initial node setup - Startup-level values for realistic scenarios
const initialNodes = [
  // Top row - Revenue loop
  { id: 'listeners', type: 'editable', position: { x: 400, y: 20 }, data: { name: 'Listeners', value: 100, displayValue: '100', background: '#c084fc', borderColor: '#e9d5ff' } },
  { id: 'revenue', type: 'editable', position: { x: 700, y: 20 }, data: { name: 'Revenue', value: 1000, displayValue: '$1k', background: '#60a5fa', borderColor: '#bfdbfe' } },
  { id: 'artistIncome', type: 'editable', position: { x: 1000, y: 20 }, data: { name: 'Artist Income', value: 800, displayValue: '$800', background: '#f0abfc', borderColor: '#fce7f3' } },

  // Middle row - Quality and stores
  { id: 'storeAttention', type: 'editable', position: { x: 1000, y: 180 }, data: { name: 'Store Attention', value: 95, displayValue: '95%', background: '#34d399', borderColor: '#a7f3d0' } },
  { id: 'stores', type: 'editable', position: { x: 1000, y: 340 }, data: { name: 'Stores', value: 2, displayValue: '2', background: '#34d399', borderColor: '#a7f3d0' } },
  { id: 'quality', type: 'editable', position: { x: 700, y: 340 }, data: { name: 'Quality', value: 65, displayValue: '65%', background: '#818cf8', borderColor: '#c7d2fe' } },
  { id: 'overhead', type: 'editable', position: { x: 400, y: 340 }, data: { name: 'Overhead', value: 25, displayValue: '25%', background: '#ef4444', borderColor: '#fca5a5' } },

  // Left column - Artists and community
  { id: 'artists', type: 'editable', position: { x: 50, y: 180 }, data: { name: 'Artists', value: 50, displayValue: '50', background: '#f9a8d4', borderColor: '#fbcfe8' } },
  { id: 'leadTime', type: 'editable', position: { x: 50, y: 340 }, data: { name: 'Lead Time', value: 31, displayValue: '31 days', background: '#a78bfa', borderColor: '#ddd6fe' } },
  { id: 'community', type: 'editable', position: { x: 50, y: 500 }, data: { name: 'Community', value: 60, displayValue: '60%', background: '#fbbf24', borderColor: '#fde68a' } },
  { id: 'cohesion', type: 'editable', position: { x: 50, y: 660 }, data: { name: 'Cohesion', value: 88, displayValue: '88%', background: '#fbbf24', borderColor: '#fde68a' } },

  // Bottom row - Reputation and sustainability
  { id: 'reputation', type: 'editable', position: { x: 400, y: 660 }, data: { name: 'Reputation', value: 60, displayValue: '60%', background: '#fb923c', borderColor: '#fed7aa' } },
  { id: 'sustainability', type: 'editable', position: { x: 700, y: 500 }, data: { name: 'Sustainability', value: 63, displayValue: '63%', background: '#10b981', borderColor: '#6ee7b7' } },

  // Loop identifiers - Reinforcing loops (green)
  { id: 'loop-r1', type: 'loopIdentifier', position: { x: 200, y: 150 }, data: { label: 'R1: Revenue Growth', background: '#d1fae5', borderColor: '#10b981', textColor: '#065f46' }, draggable: true },
  { id: 'loop-r2', type: 'loopIdentifier', position: { x: 400, y: 200 }, data: { label: 'R2: Quality Reinforcement', background: '#d1fae5', borderColor: '#10b981', textColor: '#065f46' }, draggable: true },
  { id: 'loop-r3', type: 'loopIdentifier', position: { x: 150, y: 350 }, data: { label: 'R3: Community Network', background: '#d1fae5', borderColor: '#10b981', textColor: '#065f46' }, draggable: true },
  { id: 'loop-r4', type: 'loopIdentifier', position: { x: 1000, y: 200 }, data: { label: 'R4: B2B Growth', background: '#d1fae5', borderColor: '#10b981', textColor: '#065f46' }, draggable: true },

  // Loop identifiers - Balancing loops (red/orange)
  { id: 'loop-b1', type: 'loopIdentifier', position: { x: 600, y: 570 }, data: { label: 'B1: Artist Churn', background: '#fee2e2', borderColor: '#ef4444', textColor: '#991b1b' }, draggable: true },
  { id: 'loop-b2', type: 'loopIdentifier', position: { x: 380, y: 350 }, data: { label: 'B2: Overhead Limits', background: '#fee2e2', borderColor: '#ef4444', textColor: '#991b1b' }, draggable: true },
  { id: 'loop-b3', type: 'loopIdentifier', position: { x: 180, y: 550 }, data: { label: 'B3: Fragmentation', background: '#fed7aa', borderColor: '#f59e0b', textColor: '#92400e' }, draggable: true },
  { id: 'loop-b4', type: 'loopIdentifier', position: { x: 580, y: 350 }, data: { label: 'B4: Lead Time Drag', background: '#e9d5ff', borderColor: '#a78bfa', textColor: '#5b21b6' }, draggable: true },
  { id: 'loop-b5', type: 'loopIdentifier', position: { x: 850, y: 180 }, data: { label: 'B5: Store Dilution', background: '#ccfbf1', borderColor: '#14b8a6', textColor: '#115e59' }, draggable: true },
];

// Flow types for different relationship kinds
const FLOW_TYPES = {
  MONEY: { speed: 'fast', animated: true, strokeDasharray: '5,5' },      // Fast dashed - financial flows
  INFLUENCE: { speed: 'medium', animated: true, strokeDasharray: '3,3' }, // Medium dashed - social/reputation
  CREATION: { speed: 'slow', animated: true, strokeDasharray: '8,2' },    // Slow sparse dash - creative processes
  SUPPORT: { speed: 'medium', animated: true, strokeDasharray: '2,2' },   // Medium dense dash - infrastructure
};

const createEdge = (id, source, target, label, flowType = null, color = '#6b7280', edgeType = 'default', sourceHandle = null, targetHandle = null) => {
  // Detect polarity from label
  const isNegative = label.includes('(-)');
  const polarity = isNegative ? 'negative' : 'positive';

  const baseEdge = {
    id,
    source,
    target,
    label,
    type: edgeType,
    markerEnd: { type: MarkerType.ArrowClosed, color },
    labelStyle: {
      fontSize: 10,
      fontWeight: 500,
      // Make negative labels more distinct
      color: isNegative ? '#dc2626' : '#374151'
    },
    labelBgStyle: {
      fill: 'white',
      fillOpacity: 0.9,
      // Add subtle background tint for negative edges
      ...(isNegative && { fill: '#fef2f2' })
    },
    labelBgPadding: [4, 6],
    labelBgBorderRadius: 3,
  };

  // Add source/target handles if specified (for bidirectional edge spacing)
  if (sourceHandle) baseEdge.sourceHandle = sourceHandle;
  if (targetHandle) baseEdge.targetHandle = targetHandle;

  if (flowType) {
    const flow = FLOW_TYPES[flowType];
    // Use different dash pattern for negative relationships
    const dasharray = isNegative ? '10,5' : flow.strokeDasharray;

    return {
      ...baseEdge,
      animated: flow.animated,
      style: {
        stroke: color,
        strokeWidth: isNegative ? 2.5 : 2, // Slightly thicker for negative
        strokeDasharray: dasharray,
        animationDuration: flow.speed === 'fast' ? '1s' : flow.speed === 'medium' ? '2s' : '3s'
      },
      data: { flowType: flowType, polarity: polarity }
    };
  }

  return {
    ...baseEdge,
    animated: false,
    style: {
      stroke: color,
      strokeWidth: isNegative ? 2.5 : 2,
      strokeDasharray: isNegative ? '10,5' : undefined
    },
    data: { polarity: polarity }
  };
};

const initialEdges = [
  // R1: Revenue Growth Loop (GREEN - Reinforcing)
  createEdge('e1', 'listeners', 'revenue', 'Subscribe (+)', 'MONEY', '#10b981', 'smoothstep', 'right', 'left'),
  createEdge('e2', 'revenue', 'artistIncome', '80% (+)', 'MONEY', '#10b981', 'smoothstep', 'right', 'left'),

  // R2: Quality Reinforcement Loop (GREEN - Reinforcing)
  createEdge('e3', 'artistIncome', 'quality', 'Resources (+)', 'CREATION', '#10b981', 'smoothstep', 'bottom', 'top'),
  createEdge('e4', 'quality', 'listeners', 'Attracts (+)', 'INFLUENCE', '#10b981', 'smoothstep', 'top', 'bottom'),
  createEdge('e5', 'artists', 'quality', 'Create (+)', 'CREATION', '#10b981', 'smoothstep', 'right', 'left'),

  // R3: Community Network Loop (GREEN - Reinforcing)
  createEdge('e6', 'artists', 'community', 'Participate (+)', 'SUPPORT', '#10b981', 'smoothstep', 'bottom', 'top'),
  createEdge('e7', 'community', 'artists', 'Mutual aid (+)', 'SUPPORT', '#10b981', 'smoothstep', 'top', 'left'),
  createEdge('e8', 'community', 'reputation', 'Builds (+)', 'INFLUENCE', '#10b981', 'smoothstep', 'bottom', 'top'),
  createEdge('e9', 'reputation', 'artists', 'Attracts (+)', 'INFLUENCE', '#10b981', 'smoothstep', 'left', 'bottom'),
  createEdge('e10', 'reputation', 'listeners', 'Attracts (+)', 'INFLUENCE', '#10b981', 'smoothstep', 'right', 'left'),

  // R4: B2B Growth Loop (GREEN - Reinforcing) - Stores → Revenue → Quality → Stores
  createEdge('e11', 'stores', 'revenue', 'B2B fees (+)', 'MONEY', '#10b981', 'smoothstep', 'left', 'right'),
  // Revenue → Quality already covered by e3 (via Artist Income)
  createEdge('e13', 'quality', 'stores', 'Attracts (+)', 'INFLUENCE', '#10b981', 'smoothstep', 'right', 'left'),

  // Other reinforcing relationships (GREEN)
  createEdge('e14', 'stores', 'listeners', 'Stock vinyl (+)', 'SUPPORT', '#10b981', 'smoothstep', 'top', 'right'),
  createEdge('e16', 'artists', 'artistIncome', 'Tour $ (+)', 'MONEY', '#10b981', 'smoothstep', 'top', 'bottom'),
  createEdge('e17', 'artists', 'sustainability', 'Bio-vinyl (+)', 'SUPPORT', '#10b981', 'smoothstep', 'right', 'left'),
  createEdge('e18', 'sustainability', 'reputation', 'Green image (+)', 'INFLUENCE', '#10b981', 'smoothstep', 'left', 'top'),
  createEdge('e19', 'listeners', 'sustainability', 'Eco-conscious (+)', 'SUPPORT', '#10b981', 'smoothstep', 'right', 'left'),

  // B2: Overhead Limits Loop (RED - Balancing) - Artists → Overhead → Quality → [Artists via e5]
  createEdge('e20', 'artists', 'overhead', 'Burden (+)', 'SUPPORT', '#ef4444', 'smoothstep', 'right', 'top'),
  createEdge('e21', 'overhead', 'quality', 'Degrades (-)', 'CREATION', '#ef4444', 'smoothstep', 'right', 'left'),
  // Quality → Artists already covered by e5

  // B3: Community Fragmentation Loop (ORANGE - Balancing) - Artists → Cohesion → Reputation → Artists
  createEdge('e22', 'artists', 'cohesion', 'Fragments (-)', 'INFLUENCE', '#f59e0b', 'smoothstep', 'bottom', 'top'),
  createEdge('e23', 'cohesion', 'reputation', 'Strengthens (+)', 'INFLUENCE', '#f59e0b', 'smoothstep', 'right', 'left'),
  // Reputation → Artists already covered by e9

  // B4: Manufacturing Lead Time Loop (PURPLE - Balancing) - Artists → Lead Time → Artists
  createEdge('e24', 'artists', 'leadTime', 'Delays (+)', 'SUPPORT', '#a78bfa', 'smoothstep', 'left', 'top'),
  createEdge('e25', 'leadTime', 'artists', 'Frustrates (-)', 'INFLUENCE', '#a78bfa', 'smoothstep', 'right', 'top'),
  // Complete loop! ✓

  // B5: Store Attention Dilution Loop (TEAL - Balancing) - Artists → Store Attention → Stores → [back to Artists somehow?]
  createEdge('e26', 'artists', 'storeAttention', 'Dilutes (-)', 'INFLUENCE', '#14b8a6', 'smoothstep', 'top', 'left'),
  createEdge('e27', 'storeAttention', 'stores', 'Attracts (+)', 'INFLUENCE', '#14b8a6', 'smoothstep', 'bottom', 'top'),
  // Need: Stores → Artists (via visibility/promotion)
  createEdge('e28', 'stores', 'artists', 'Promotes (+)', 'SUPPORT', '#14b8a6', 'smoothstep', 'left', 'top'),
];

export default function CausalLoopSimulator() {
  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);
  const [simulationRunning, setSimulationRunning] = useState(false);
  const [iteration, setIteration] = useState(0);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [simulationData, setSimulationData] = useState(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [isSendingMessage, setIsSendingMessage] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [showAbout, setShowAbout] = useState(false);
  const [showNodeInfo, setShowNodeInfo] = useState(null);
  const [showEdgeInfo, setShowEdgeInfo] = useState(null);
  const [showLoopInfo, setShowLoopInfo] = useState(null);
  const [highlightedLoop, setHighlightedLoop] = useState(null);
  const [animationsEnabled, setAnimationsEnabled] = useState(true);
  const [simulationMode, setSimulationMode] = useState('static'); // 'static' or 'predictive'
  const [timePeriod, setTimePeriod] = useState('1year'); // '1month', '3months', '6months', '1year', '2years'
  const [iterationDuration, setIterationDuration] = useState(200); // milliseconds per iteration

  const messagesEndRef = useRef(null);

  // Auto-scroll to bottom when messages change
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, isAnalyzing, isSendingMessage]);

  // Time period configurations (iterations and what each represents)
  const timeConfigs = {
    '1month': { iterations: 30, label: '1 Month', iterationLabel: '1 day' },
    '3months': { iterations: 90, label: '3 Months', iterationLabel: '1 day' },
    '6months': { iterations: 180, label: '6 Months', iterationLabel: '1 day' },
    '1year': { iterations: 52, label: '1 Year', iterationLabel: '1 week' },
    '2years': { iterations: 104, label: '2 Years', iterationLabel: '1 week' }
  };

  // Initialize Anthropic client
  const anthropic = new Anthropic({
    apiKey: process.env.REACT_APP_ANTHROPIC_API_KEY,
    dangerouslyAllowBrowser: true
  });

  const updateNodeValue = useCallback((nodeId, newValue) => {
    setNodes((nds) =>
      nds.map((node) => {
        if (node.id === nodeId) {
          let displayValue = newValue;
          
          if (nodeId === 'revenue' || nodeId === 'artistIncome') {
            displayValue = `$${Math.round(newValue / 1000)}k`;
          } else if (nodeId === 'quality' || nodeId === 'community' || nodeId === 'reputation' || nodeId === 'sustainability') {
            displayValue = `${Math.round(newValue)}%`;
          } else if (nodeId === 'listeners') {
            displayValue = newValue.toLocaleString();
          } else {
            displayValue = Math.round(newValue).toString();
          }

          return {
            ...node,
            data: {
              ...node.data,
              value: newValue,
              displayValue: displayValue,
            },
          };
        }
        return node;
      })
    );
  }, [setNodes]);

const simulate = useCallback((mode = 'static') => {
  setNodes((currentNodes) => {
    // Take snapshot of current values
    const nodeValues = {};
    currentNodes.forEach((node) => {
      nodeValues[node.id] = node.data.value;
    });

    // Calculate NEW values based on snapshot (not modifying in place!)
    const newValues = { ...nodeValues };

    const listeners = nodeValues.listeners;
    const revenue = nodeValues.revenue;
    const artistIncome = nodeValues.artistIncome;
    const quality = nodeValues.quality;
    const artists = nodeValues.artists;
    const community = nodeValues.community;
    const reputation = nodeValues.reputation;
    const stores = nodeValues.stores;
    const sustainability = nodeValues.sustainability;
    const overhead = nodeValues.overhead;
    const cohesion = nodeValues.cohesion;
    const leadTime = nodeValues.leadTime;
    const storeAttention = nodeValues.storeAttention;

    // Calculate all new values (order matters for dependencies)
    // First calculate stores effects
    const storeB2BFees = stores * 50; // Stores pay B2B subscription

    newValues.revenue = (listeners * 10) + storeB2BFees;

    newValues.artistIncome = newValues.revenue * 0.8;

    const avgIncomePerArtist = newValues.artistIncome / Math.max(artists, 1);

    // B2: Operational Overhead Loop - more artists = more burden = lower quality
    newValues.overhead = Math.min(100, 20 + (artists / 10)); // Overhead increases with artists
    const qualityFromIncome = Math.min(100, 50 + (avgIncomePerArtist / 100));
    const qualityFromArtists = Math.min(100, 40 + Math.log(artists + 1) * 10);
    const qualityLossFromOverhead = newValues.overhead * 0.3; // Overhead degrades quality
    newValues.quality = Math.max(0, (qualityFromIncome + qualityFromArtists) / 2 - qualityLossFromOverhead);

    // B3: Community Fragmentation Loop - more artists = less cohesion
    const baseCohesion = Math.min(100, 90 - (artists / 20)); // Cohesion decreases as artists grow
    newValues.cohesion = Math.max(30, baseCohesion); // Min 30% cohesion

    // B4: Manufacturing Lead Time Loop - more artists = longer waits
    const baseLeadTime = 30 + (artists / 50); // Lead time increases with vinyl demand
    newValues.leadTime = Math.min(120, baseLeadTime); // Max 120 days

    // B5: Store Attention Dilution Loop - more artists = less attention per artist
    const catalogSize = artists;
    newValues.storeAttention = Math.max(10, 100 - (catalogSize / 10)); // Attention decreases

    // Calculate community and sustainability (needed for reputation)
    newValues.community = Math.min(100, 30 + Math.log(artists + 1) * 15);
    newValues.sustainability = Math.min(100, 60 + (artists / 20));

    // Calculate reputation (depends on cohesion, sustainability, quality)
    const reputationFromCohesion = newValues.cohesion * 0.4;
    const reputationFromSustainability = newValues.sustainability * 0.3;
    const reputationFromQuality = newValues.quality * 0.3;
    newValues.reputation = reputationFromCohesion + reputationFromSustainability + reputationFromQuality;

    if (mode === 'predictive') {
      // PREDICTIVE MODE: Apply growth multipliers to simulate time progression
      const listenerGrowthFromQuality = newValues.quality > 70 ? 1.02 : newValues.quality > 50 ? 1.01 : 1.0;
      const listenerGrowthFromReputation = newValues.reputation > 70 ? 1.015 : 1.0;
      const listenerGrowthFromStores = stores > 0 ? 1 + (stores * 0.0001) : 1.0;
      newValues.listeners = listeners * listenerGrowthFromQuality * listenerGrowthFromReputation * listenerGrowthFromStores;

      // Artists grow/shrink over time (affected by lead time frustration - B4)
      const leadTimePenalty = newValues.leadTime > 60 ? 0.98 : 1.0; // Long waits slow growth
      if (avgIncomePerArtist > 250 && newValues.reputation > 70) {
        newValues.artists = artists * 1.03 * leadTimePenalty;
      } else if (avgIncomePerArtist < 100) {
        newValues.artists = artists * 0.98;
      } else {
        newValues.artists = artists * 1.0 * leadTimePenalty;
      }

      // Stores grow over time (attracted by store attention - B5)
      const storeAttentionBonus = newValues.storeAttention > 50 ? 1.02 : 1.0; // High attention attracts stores
      if (newValues.quality > 70 && artists > 80) {
        newValues.stores = stores * storeAttentionBonus;
      } else {
        newValues.stores = stores * 1.0;
      }
    } else {
      // STATIC MODE: No growth multipliers - just show immediate cause-and-effect
      // Listeners, artists, and stores stay constant in static mode
      newValues.listeners = listeners;
      newValues.artists = artists;
      newValues.stores = stores;
    }

    // Apply new values to nodes
    return currentNodes.map((node) => {
      const newValue = newValues[node.id];
      let displayValue = newValue;

      if (node.id === 'revenue' || node.id === 'artistIncome') {
        displayValue = `$${Math.round(newValue / 1000)}k`;
      } else if (['quality', 'community', 'reputation', 'sustainability', 'overhead', 'cohesion', 'storeAttention'].includes(node.id)) {
        displayValue = `${Math.round(newValue)}%`;
      } else if (node.id === 'leadTime') {
        displayValue = `${Math.round(newValue)} days`;
      } else if (node.id === 'listeners') {
        displayValue = Math.round(newValue).toLocaleString();
      } else {
        displayValue = Math.round(newValue).toString();
      }

      return {
        ...node,
        data: {
          ...node.data,
          value: newValue,
          displayValue: displayValue,
        },
      };
    });
  });

  setIteration((prev) => prev + 1);
}, [setNodes]);

const runSimulation = useCallback(() => {
  if (simulationRunning) return;

  setSimulationRunning(true);
  setIteration(0);

  // Capture initial state directly from nodes
  const initialSnapshot = {};
  nodes.forEach((node) => {
    initialSnapshot[node.id] = node.data.value;
  });

  // Determine max iterations based on mode
  const maxIterations = simulationMode === 'static' ? 1 : timeConfigs[timePeriod].iterations;

  let iterCount = 0;

  const runIteration = () => {
    simulate(simulationMode);
    iterCount++;
    setIteration(iterCount);

    // Stop when max iterations reached
    if (iterCount >= maxIterations) {
      setSimulationRunning(false);

      // Wait longer for final state to update, then capture and analyze
      setTimeout(() => {
        const finalSnapshot = {};
        setNodes((currentNodes) => {
          currentNodes.forEach((node) => {
            finalSnapshot[node.id] = node.data.value;
          });
          return currentNodes;
        });

        // Build history with initial and final states
        const history = [initialSnapshot, finalSnapshot];

        const finalIteration = iterCount;
        setSimulationData({
          history,
          finalIteration,
          mode: simulationMode,
          timePeriod: simulationMode === 'predictive' ? timeConfigs[timePeriod].label : 'Static',
          iterationLabel: simulationMode === 'predictive' ? timeConfigs[timePeriod].iterationLabel : 'N/A'
        });
        analyzeResults({
          history,
          finalIteration,
          mode: simulationMode,
          timePeriod: simulationMode === 'predictive' ? timeConfigs[timePeriod].label : 'Static',
          iterationLabel: simulationMode === 'predictive' ? timeConfigs[timePeriod].iterationLabel : 'N/A'
        });
      }, 500);
    } else {
      // Continue iterations
      setTimeout(runIteration, iterationDuration);
    }
  };

  // Start first iteration
  setTimeout(runIteration, iterationDuration);

}, [simulationRunning, nodes, simulate, simulationMode, timePeriod, timeConfigs, iterationDuration, setNodes]);

  const analyzeResults = async (data) => {
    setIsAnalyzing(true);

    const finalState = data.history[data.history.length - 1];
    const initialState = data.history[0];

    const modeDescription = data.mode === 'static'
      ? 'STATIC MODE: Showing immediate cause-and-effect relationships (no time progression)'
      : `PREDICTIVE MODE: Simulating ${data.timePeriod} forward in time (${data.finalIteration} ${data.iterationLabel} periods)`;

    // Calculate changes for static mode
    const changes = {};
    if (data.mode === 'static') {
      Object.keys(initialState).forEach(key => {
        const initial = initialState[key];
        const final = finalState[key];
        const diff = final - initial;
        const pctChange = initial !== 0 ? ((diff / initial) * 100).toFixed(1) : 'N/A';
        if (Math.abs(diff) > 0.01) {
          changes[key] = { initial, final, diff, pctChange };
        }
      });
    }

    const prompt = `You are analyzing a causal loop simulation for a music cooperative. The user edited some variables to explore a "what-if" scenario.

${modeDescription}

Initial State (user's starting parameters):
${JSON.stringify(initialState, null, 2)}

Final State (after simulation recalculated dependent variables):
${JSON.stringify(finalState, null, 2)}

${data.mode === 'static' && Object.keys(changes).length > 0 ? `
Variables that changed:
${Object.entries(changes).map(([key, vals]) =>
  `- ${key}: ${vals.initial.toFixed(0)} → ${vals.final.toFixed(0)} (${vals.diff > 0 ? '+' : ''}${vals.diff.toFixed(0)}, ${vals.pctChange}%)`
).join('\n')}
` : ''}

Please explain the results objectively:
1. **What changed**: Summarize which variables changed and by how much${data.mode === 'static' ? ' (focus on the cascade of effects through the system)' : ''}
2. **Why it happened**: Explain the causal relationships - which edges/loops caused these changes
3. **Feedback loops**: Identify which reinforcing (R1-R4) and balancing (B1-B5) loops were activated
4. **Key insight**: One sentence about the most important system dynamic revealed

Use neutral, explanatory language. This is a simulation experiment, not a business decision to judge. Keep it concise (max 250 words).`;

    try {
      const message = await anthropic.messages.create({
        model: 'claude-sonnet-4-20250514',
        max_tokens: 1024,
        messages: [{ role: 'user', content: prompt }],
      });

      const analysis = message.content[0].text;
      setMessages([{ role: 'assistant', content: analysis }]);
    } catch (error) {
      console.error('Error analyzing results:', error);
      setMessages([{ 
        role: 'assistant', 
        content: 'Analysis complete! The simulation ran for ' + data.finalIteration + ' iterations. Ask me questions about what you observe!' 
      }]);
    } finally {
      setIsAnalyzing(false);
    }
  };

  const sendMessage = async () => {
    if (!inputMessage.trim() || !simulationData || isSendingMessage) return;

    const userMessage = { role: 'user', content: inputMessage };
    setMessages((prev) => [...prev, userMessage]);
    setInputMessage('');
    setIsSendingMessage(true);

    const contextPrompt = `The user is working with a music cooperative simulation. Here's the simulation data:

Final State: ${JSON.stringify(simulationData.history[simulationData.history.length - 1], null, 2)}
Iterations: ${simulationData.finalIteration}

User question: ${inputMessage}

Provide a helpful, concise answer based on the simulation results.`;

    try {
      const message = await anthropic.messages.create({
        model: 'claude-sonnet-4-20250514',
        max_tokens: 1024,
        messages: [{ role: 'user', content: contextPrompt }],
      });

      const response = message.content[0].text;
      setMessages((prev) => [...prev, { role: 'assistant', content: response }]);
    } catch (error) {
      console.error('Error sending message:', error);
      setMessages((prev) => [...prev, {
        role: 'assistant',
        content: 'Sorry, I encountered an error. Please try again.'
      }]);
    } finally {
      setIsSendingMessage(false);
    }
  };

  const resetSimulation = () => {
    setNodes(initialNodes);
    setEdges(initialEdges);
    setIteration(0);
    setMessages([]);
    setSimulationData(null);
    setSimulationRunning(false);
  };

  // Define which nodes and edges belong to each loop, with their highlight colors
  const loopMembership = {
    'loop-r1': {
      nodes: ['listeners', 'revenue', 'artistIncome', 'quality'],
      edges: ['e1', 'e2', 'e3', 'e4'],
      color: '#10b981' // Green for reinforcing
    },
    'loop-r2': {
      nodes: ['listeners', 'revenue', 'artistIncome', 'quality', 'artists'],
      edges: ['e1', 'e2', 'e3', 'e4', 'e5'],
      color: '#10b981' // Green for reinforcing
    },
    'loop-r3': {
      nodes: ['artists', 'community', 'reputation'],
      edges: ['e6', 'e7', 'e8', 'e9'],
      color: '#10b981' // Green for reinforcing
    },
    'loop-r4': {
      nodes: ['stores', 'revenue', 'listeners', 'quality'],
      edges: ['e11', 'e12', 'e13', 'e4', 'e1'],
      color: '#10b981' // Green for reinforcing
    },
    'loop-b1': {
      nodes: ['artists', 'artistIncome', 'revenue', 'listeners', 'quality'],
      edges: ['e1', 'e2', 'e3', 'e4', 'e5'],
      color: '#10b981' // B1 uses green (same edges as R loops)
    },
    'loop-b2': {
      nodes: ['artists', 'overhead', 'quality'],
      edges: ['e20', 'e21'],
      color: '#ef4444' // Red for balancing
    },
    'loop-b3': {
      nodes: ['artists', 'cohesion', 'reputation'],
      edges: ['e22', 'e23', 'e9'],
      color: '#f59e0b' // Orange for balancing
    },
    'loop-b4': {
      nodes: ['artists', 'leadTime'],
      edges: ['e24', 'e25'],
      color: '#a78bfa' // Purple for balancing
    },
    'loop-b5': {
      nodes: ['artists', 'storeAttention', 'stores'],
      edges: ['e26', 'e27'],
      color: '#14b8a6' // Teal for balancing
    },
  };

  // Helper function to convert hex to rgba
  const hexToRgba = (hex, alpha) => {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  };

  const updatedNodes = nodes.map((node) => {
    const isHighlighted = highlightedLoop && loopMembership[highlightedLoop]?.nodes.includes(node.id);
    const isLoopLabel = node.type === 'loopIdentifier';
    const isDimmed = highlightedLoop && !isHighlighted && !isLoopLabel;
    const loopColor = highlightedLoop && loopMembership[highlightedLoop]?.color;

    return {
      ...node,
      style: {
        ...node.style,
        opacity: isDimmed ? 0.3 : 1,
        filter: isHighlighted && loopColor ? `drop-shadow(0 0 8px ${hexToRgba(loopColor, 0.6)})` : 'none',
      },
      data: {
        ...node.data,
        onValueChange: (newValue) => updateNodeValue(node.id, newValue),
        onClick: () => {
          if (node.type === 'loopIdentifier') {
            setShowLoopInfo(node);
            setHighlightedLoop(node.id);
          } else {
            setShowNodeInfo(node);
          }
        },
      },
    };
  });

  const updatedEdges = edges.map((edge) => {
    const isHighlighted = highlightedLoop && loopMembership[highlightedLoop]?.edges.includes(edge.id);
    const isDimmed = highlightedLoop && !isHighlighted;
    const loopColor = highlightedLoop && loopMembership[highlightedLoop]?.color;

    return {
      ...edge,
      animated: animationsEnabled && edge.animated && (highlightedLoop ? isHighlighted : true),
      style: {
        ...edge.style,
        opacity: isDimmed ? 0.2 : 1,
        strokeWidth: isHighlighted ? (edge.style?.strokeWidth || 2) * 1.5 : edge.style?.strokeWidth || 2,
        filter: isHighlighted && loopColor ? `drop-shadow(0 0 4px ${hexToRgba(loopColor, 0.6)})` : 'none',
      },
    };
  });

  const onEdgeClick = useCallback((event, edge) => {
    setShowEdgeInfo(edge);
  }, []);

  const nodeDescriptions = {
    listeners: 'The number of active subscribers to the music cooperative streaming platform. They pay monthly subscriptions and discover new music.',
    revenue: 'Total monthly revenue generated from listener subscriptions and B2B partnerships with stores.',
    artistIncome: 'Total income distributed to all artists (80% of platform revenue). Fair compensation is the core mission.',
    quality: 'Overall quality of music catalog and platform services. Improves with resources but degrades with operational overhead.',
    artists: 'Number of musicians in the cooperative. Growth depends on fair income and strong reputation, but creates operational challenges.',
    community: 'Strength of mutual support, collaboration, and shared resources among artists. Grows with artist participation.',
    reputation: 'The cooperative\'s public image, built from community cohesion (40%), sustainability (30%), and quality (30%).',
    stores: 'Independent record stores partnering with the co-op. They stock physical media and subscribe to booking intelligence.',
    sustainability: 'Environmental commitment score, driven by bio-based vinyl adoption and artist participation in green initiatives.',
    overhead: 'Operational burden from supporting more artists - moderation, support, fraud detection. Higher overhead degrades platform quality.',
    cohesion: 'Community unity and intimacy. More artists create genre diversity, which can fragment the sense of "we\'re all in this together".',
    leadTime: 'Wait time for vinyl manufacturing (in days). More artists ordering vinyl increases demand and delays at pressing plants.',
    storeAttention: 'Per-artist visibility and shelf space at partner stores. More artists competing for finite store attention dilutes promotion.',
  };

  const edgeDescriptions = {
    e1: 'Listeners pay monthly subscriptions ($10/month assumed), generating revenue for the cooperative.',
    e2: '80% of platform revenue goes directly to artists - the fair compensation model that defines the cooperative.',
    e3: 'Higher artist income provides resources for better equipment, production, and time to create quality music.',
    e4: 'Better music quality attracts new listeners through word-of-mouth and music discovery.',
    e5: 'More artists create more music, expanding and diversifying the catalog quality.',
    e6: 'Artists participate in the cooperative community, contributing to its strength.',
    e7: 'Strong community provides mutual support, helping artists stay engaged and productive.',
    e8: 'Active, collaborative community builds the cooperative\'s reputation as a values-driven organization.',
    e9: 'Strong reputation attracts new artists who want to join a fair, community-focused platform.',
    e10: 'Good reputation attracts listeners who want to support ethical, artist-friendly music platforms.',
    e11: 'Record stores pay B2B subscription fees for access to booking data and market intelligence.',
    e12: 'Platform shares sales data and listener trends with stores, helping them make inventory decisions.',
    e13: 'Stores stock vinyl and physical media, introducing their customers to cooperative artists.',
    e16: 'Artists earn additional income from touring and live performances.',
    e17: 'Artists collectively choose bio-based vinyl manufacturing, improving sustainability score.',
    e18: 'Sustainability initiatives (bio-vinyl, green practices) enhance the cooperative\'s ethical reputation.',
    e19: 'Environmentally-conscious listeners support the platform specifically for its sustainability commitment.',
    e20: 'More artists require more moderation, fraud detection, support tickets, and platform resources - increasing operational burden.',
    e21: 'High operational overhead diverts resources from development and quality improvements, degrading platform services.',
    e22: 'More artists means more genre diversity (punk vs metal vs ska), which can fragment community and reduce sense of unity.',
    e23: 'Strong community cohesion (not just size) builds reputation as artists feel genuinely connected and supported.',
    e24: 'More artists ordering vinyl overwhelms pressing plant capacity, increasing wait times for manufacturing.',
    e25: 'Long wait times frustrate artists, reducing satisfaction and making them less likely to stay or recommend the platform.',
    e26: 'More artists competing for finite store shelf space means each individual artist gets less visibility and promotion.',
    e27: 'Higher per-artist attention from stores makes partnership more valuable, attracting more stores to join the cooperative.',
  };

  const loopDescriptions = {
    'loop-r1': {
      name: 'R1: Revenue Growth',
      type: 'Reinforcing',
      description: 'More listeners generate more revenue, which supports better artist income and quality, attracting even more listeners. This virtuous cycle drives exponential growth when conditions are favorable.',
      path: 'Listeners → Revenue → Artist Income → Quality → Listeners'
    },
    'loop-r2': {
      name: 'R2: Quality Reinforcement',
      type: 'Reinforcing',
      description: 'Higher quality music attracts more listeners, generating more revenue for artists to create even better quality content. This loop amplifies platform growth through continuous quality improvement.',
      path: 'Quality → Listeners → Revenue → Artist Income → Quality'
    },
    'loop-r3': {
      name: 'R3: Community Network',
      type: 'Reinforcing',
      description: 'Artists build strong community connections, which enhances reputation and attracts more artists, further strengthening the community. This creates a self-reinforcing network effect.',
      path: 'Artists → Community → Reputation → Artists'
    },
    'loop-r4': {
      name: 'R4: B2B Growth',
      type: 'Reinforcing',
      description: 'Stores pay B2B fees for market intelligence, generating revenue that improves platform quality, attracting more stores. This loop creates sustainable B2B partnerships.',
      path: 'Stores → Revenue → Quality → Stores'
    },
    'loop-b1': {
      name: 'B1: Artist Churn',
      type: 'Balancing',
      description: 'When income per artist drops too low (below $100/month), artists leave the platform, slowing growth and preventing unsustainable expansion. This negative feedback maintains artist quality of life.',
      path: 'Artists ↑ → Income per Artist ↓ → Artist Churn ↑'
    },
    'loop-b2': {
      name: 'B2: Overhead Limits',
      type: 'Balancing',
      description: 'More artists create operational overhead (moderation, support, fraud detection), which degrades platform quality and slows listener growth. This limits how fast the platform can scale.',
      path: 'Artists ↑ → Overhead ↑ → Quality ↓'
    },
    'loop-b3': {
      name: 'B3: Community Fragmentation',
      type: 'Balancing',
      description: 'As more artists join, genre diversity increases, fragmenting the tight-knit community feeling. Lower cohesion weakens reputation, slowing artist growth. This maintains intimate community scale.',
      path: 'Artists ↑ → Cohesion ↓ → Reputation ↓ → Artist Growth ↓'
    },
    'loop-b4': {
      name: 'B4: Lead Time Drag',
      type: 'Balancing',
      description: 'More artists ordering vinyl overwhelms pressing plant capacity, increasing lead times. Long waits frustrate artists and slow growth, preventing manufacturing bottlenecks.',
      path: 'Artists ↑ → Lead Time ↑ → Artist Frustration ↑ → Growth ↓'
    },
    'loop-b5': {
      name: 'B5: Store Dilution',
      type: 'Balancing',
      description: 'More artists compete for finite store shelf space, diluting per-artist attention. Less visibility per artist makes partnerships less attractive to stores, limiting store growth.',
      path: 'Artists ↑ → Store Attention per Artist ↓ → Store Growth ↓'
    },
  };

  return (
    <div style={{ width: '100vw', height: '100vh', display: 'flex' }}>
      <div style={{ flex: 1, position: 'relative', padding: '40px 20px 40px 40px' }}>
        <div style={{
          position: 'absolute',
          top: 10,
          left: 10,
          zIndex: 5,
          display: 'flex',
          gap: '8px',
          flexDirection: 'column'
        }}>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
            <button
              onClick={() => setShowHelp(true)}
              style={{
                padding: '8px 16px',
                background: '#3b82f6',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: 600,
              }}
            >
              ❓ Help
            </button>
            <button
              onClick={() => setShowAbout(true)}
              style={{
                padding: '8px 16px',
                background: '#8b5cf6',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: 600,
              }}
            >
              ℹ️ About
            </button>
            <button
              onClick={() => setAnimationsEnabled(!animationsEnabled)}
              style={{
                padding: '8px 16px',
                background: animationsEnabled ? '#10b981' : '#6b7280',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: 600,
              }}
            >
              🎬 Animations
            </button>

            <span style={{
              fontSize: '14px',
              fontWeight: 600,
              color: '#374151',
              display: 'flex',
              alignItems: 'center',
              height: '38px'
            }}>
              Mode:
            </span>
            <button
              onClick={() => setSimulationMode('static')}
              style={{
                padding: '8px 16px',
                background: simulationMode === 'static' ? '#3b82f6' : '#e5e7eb',
                color: simulationMode === 'static' ? 'white' : '#6b7280',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: 600,
              }}
            >
              📊 Static
            </button>
            <button
              onClick={() => setSimulationMode('predictive')}
              style={{
                padding: '8px 16px',
                background: simulationMode === 'predictive' ? '#3b82f6' : '#e5e7eb',
                color: simulationMode === 'predictive' ? 'white' : '#6b7280',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: 600,
              }}
            >
              📈 Predictive
            </button>
          </div>

          {simulationMode === 'predictive' && (
            <div style={{
              background: 'white',
              padding: '10px 12px',
              borderRadius: '6px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              display: 'flex',
              gap: '8px',
              alignItems: 'center'
            }}>
              <span style={{ fontSize: '12px', fontWeight: 600, color: '#374151' }}>Time Period:</span>
              <select
                value={timePeriod}
                onChange={(e) => setTimePeriod(e.target.value)}
                style={{
                  padding: '6px 8px',
                  border: '1px solid #d1d5db',
                  borderRadius: '4px',
                  fontSize: '12px',
                  cursor: 'pointer',
                }}
              >
                <option value="1month">1 Month (30 days)</option>
                <option value="3months">3 Months (90 days)</option>
                <option value="6months">6 Months (180 days)</option>
                <option value="1year">1 Year (52 weeks)</option>
                <option value="2years">2 Years (104 weeks)</option>
              </select>
              <span style={{ fontSize: '11px', color: '#6b7280', fontStyle: 'italic' }}>
                (Each iteration = {timeConfigs[timePeriod].iterationLabel})
              </span>
            </div>
          )}
        </div>

       <ReactFlow
  nodes={updatedNodes}
  edges={updatedEdges}
  onNodesChange={onNodesChange}
  onEdgesChange={onEdgesChange}
  onEdgeClick={onEdgeClick}
  onNodeClick={(event, node) => {
    event.stopPropagation();
  }}
  nodeTypes={nodeTypes}
  fitView
>
          <Controls />
          <Background variant="dots" gap={12} size={1} />
        </ReactFlow>
      </div>

      <div style={{
        width: '400px',
        background: '#f9fafb',
        borderLeft: '1px solid #e5e7eb',
        display: 'flex',
        flexDirection: 'column',
        padding: '20px',
      }}>
        <h2 style={{ marginTop: 0, marginBottom: '16px', fontSize: '20px', fontWeight: 700 }}>
          Control Panel
        </h2>

        {simulationMode === 'predictive' && (
          <div style={{
            padding: '12px',
            background: '#fef3c7',
            border: '2px solid #f59e0b',
            borderRadius: '8px',
            marginBottom: '12px',
            fontSize: '13px',
            lineHeight: '1.5'
          }}>
            <strong style={{ color: '#92400e' }}>⚠️ Predictive Mode Notice:</strong>
            <div style={{ color: '#78350f', marginTop: '4px' }}>
              Growth rates and parameters are <strong>hypothetical assumptions</strong>, not derived from historic data. Use for exploring system dynamics, not financial forecasting.
            </div>
          </div>
        )}

        <button
          onClick={runSimulation}
          disabled={simulationRunning}
          style={{
            padding: '12px 24px',
            background: simulationRunning ? '#9ca3af' : '#10b981',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            cursor: simulationRunning ? 'not-allowed' : 'pointer',
            fontSize: '16px',
            fontWeight: 600,
            marginBottom: '12px',
          }}
        >
          {simulationRunning
            ? simulationMode === 'static'
              ? '⏳ Calculating...'
              : `⏳ Running... (${iteration}/${timeConfigs[timePeriod].iterations} ${timeConfigs[timePeriod].iterationLabel}s)`
            : simulationMode === 'static'
            ? '▶️ Calculate Static State'
            : `▶️ Simulate ${timeConfigs[timePeriod].label}`
          }
        </button>

        <button
          onClick={resetSimulation}
          style={{
            padding: '12px 24px',
            background: '#ef4444',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            fontSize: '16px',
            fontWeight: 600,
            marginBottom: '20px',
          }}
        >
          🔄 Reset Simulation
        </button>

        <div style={{
          flex: 1,
          overflowY: 'auto',
          background: 'white',
          borderRadius: '8px',
          padding: '16px',
          marginBottom: '12px',
          border: '1px solid #e5e7eb',
        }}>
          <h3 style={{ marginTop: 0, marginBottom: '12px', fontSize: '16px', fontWeight: 600 }}>
            Analysis & Chat
          </h3>
          
          {isAnalyzing && (
            <div style={{ 
              padding: '12px', 
              background: '#dbeafe', 
              borderRadius: '6px',
              marginBottom: '12px',
              fontSize: '14px',
            }}>
              🤔 Analyzing simulation results...
            </div>
          )}

          {messages.length === 0 && !isAnalyzing && (
            <p style={{ color: '#6b7280', fontSize: '14px', fontStyle: 'italic' }}>
              Run a simulation to see AI analysis here. Then ask questions!
            </p>
          )}

          {messages.map((msg, idx) => (
            <div
              key={idx}
              style={{
                marginBottom: '12px',
                padding: '10px',
                borderRadius: '6px',
                background: msg.role === 'user' ? '#e0e7ff' : '#f0fdf4',
                fontSize: '14px',
                lineHeight: '1.6',
              }}
            >
              <strong>{msg.role === 'user' ? 'You' : 'AI'}:</strong>
              <div style={{ whiteSpace: 'pre-wrap', marginTop: '4px' }}>{msg.content}</div>
            </div>
          ))}

          {isSendingMessage && (
            <div style={{
              padding: '10px',
              background: '#f0fdf4',
              borderRadius: '6px',
              marginBottom: '12px',
              fontSize: '14px',
            }}>
              💬 AI is thinking...
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {simulationData && (
          <div style={{ display: 'flex', gap: '8px' }}>
            <input
              type="text"
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && !isSendingMessage && sendMessage()}
              placeholder="Ask AI about the results..."
              disabled={isSendingMessage}
              style={{
                flex: 1,
                padding: '10px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '14px',
                opacity: isSendingMessage ? 0.6 : 1,
              }}
            />
            <button
              onClick={sendMessage}
              disabled={isSendingMessage}
              style={{
                padding: '10px 20px',
                background: isSendingMessage ? '#9ca3af' : '#3b82f6',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: isSendingMessage ? 'not-allowed' : 'pointer',
                fontSize: '14px',
                fontWeight: 600,
              }}
            >
              Send
            </button>
          </div>
        )}
      </div>

      {showHelp && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          zIndex: 1000,
          paddingRight: '20px',
        }}>
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
            background: 'white',
            borderRadius: '12px',
            padding: '32px',
            width: '400px',
            maxHeight: '80vh',
            overflow: 'auto',
            boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)',
          }}>
            <h2 style={{ marginTop: 0, marginBottom: '16px', fontSize: '24px', fontWeight: 700 }}>
              How to Use the Simulator
            </h2>

            <h3 style={{ marginTop: '24px', marginBottom: '12px', fontSize: '16px', fontWeight: 600 }}>🖱️ Interacting with Nodes</h3>
            <ul style={{ marginLeft: '20px', fontSize: '14px', lineHeight: '1.8' }}>
              <li><strong>Single-click</strong> any node to see its description</li>
              <li><strong>Double-click</strong> any node to edit its value</li>
              <li>Type new value and press <strong>Enter</strong> or click away to save</li>
            </ul>

            <h3 style={{ marginTop: '24px', marginBottom: '12px', fontSize: '16px', fontWeight: 600 }}>🔗 Understanding Arrows</h3>
            <ul style={{ marginLeft: '20px', fontSize: '14px', lineHeight: '1.8' }}>
              <li><strong>Click any arrow</strong> to see what that relationship represents</li>
              <li>Arrow direction shows cause → effect</li>
              <li><strong>Positive relationships (+):</strong> Both variables move in the same direction</li>
              <li><strong>Negative relationships (-):</strong> Variables move in opposite directions (thicker, different dash pattern, red labels)</li>
            </ul>

            <h3 style={{ marginTop: '24px', marginBottom: '12px', fontSize: '16px', fontWeight: 600 }}>🔄 Feedback Loops</h3>
            <ul style={{ marginLeft: '20px', fontSize: '14px', lineHeight: '1.8' }}>
              <li><strong>Click R1-R4 labels (green)</strong> to see reinforcing loops - amplifying feedback that drives growth or decline</li>
              <li><strong>Click B1-B5 labels (colored)</strong> to see balancing loops - self-correcting feedback that creates limits</li>
              <li>Each loop has its own color: green (R1-R4), red (B2), orange (B3), purple (B4), teal (B5)</li>
              <li>Clicking a loop highlights all involved nodes and edges with the loop's color</li>
              <li>Use "Close" button to clear the highlight</li>
            </ul>

            <h3 style={{ marginTop: '24px', marginBottom: '12px', fontSize: '16px', fontWeight: 600 }}>🎬 Flow Animations</h3>
            <p style={{ marginBottom: '12px' }}>
              <strong>Toggle animations ON (green)</strong> to see all flows animate with moving dots.
            </p>
            <p style={{ marginBottom: '8px', fontSize: '14px' }}><strong>Flow Types:</strong></p>
            <ul style={{ marginLeft: '20px', fontSize: '14px', lineHeight: '1.8' }}>
              <li><strong>💰 Money</strong> (Fast, 5-5 dash): Financial transactions - subscriptions, revenue, fees</li>
              <li><strong>🎨 Creation</strong> (Slow, 8-2 dash): Artistic development - resources improving quality</li>
              <li><strong>⭐ Influence</strong> (Medium, 3-3 dash): Reputation & attraction - quality/reputation drawing people</li>
              <li><strong>🤝 Support</strong> (Medium, 2-2 dash): Infrastructure & services - data, bookings, community</li>
            </ul>
            <p style={{ marginTop: '12px', fontSize: '14px' }}>
              Different speeds reflect real-world timescales: money moves fast, creativity takes time.
            </p>

            <h3 style={{ marginTop: '24px', marginBottom: '12px', fontSize: '16px', fontWeight: 600 }}>▶️ Running Simulations</h3>
            <ul style={{ marginLeft: '20px', fontSize: '14px', lineHeight: '1.8' }}>
              <li>Click <strong>"Run Simulation"</strong> to start</li>
              <li>Simulation runs automatically until values stabilize</li>
              <li>Watch the iteration counter to see progress</li>
              <li>Claude analyzes results when complete</li>
            </ul>

            <h3 style={{ marginTop: '24px', marginBottom: '12px', fontSize: '16px', fontWeight: 600 }}>💬 Chatting with AI</h3>
            <ul style={{ marginLeft: '20px', fontSize: '14px', lineHeight: '1.8' }}>
              <li>After simulation completes, ask questions about the results</li>
              <li>AI has context about all the simulation data</li>
              <li>Ask about trends, recommendations, or "what if" scenarios</li>
            </ul>

            <h3 style={{ marginTop: '24px', marginBottom: '12px', fontSize: '16px', fontWeight: 600 }}>🎨 Node Colors</h3>
            <div style={{ fontSize: '14px', lineHeight: '1.8' }}>
              <div style={{ marginBottom: '4px' }}>🟣 Purple - Listeners & Quality</div>
              <div style={{ marginBottom: '4px' }}>🔵 Blue - Revenue & Financial Flows</div>
              <div style={{ marginBottom: '4px' }}>🩷 Pink - Artists & Artistic Value</div>
              <div style={{ marginBottom: '4px' }}>🟠 Orange - Community & Reputation</div>
              <div style={{ marginBottom: '4px' }}>🟢 Green - Stores & Sustainability</div>
            </div>

            <button
              onClick={() => setShowHelp(false)}
              style={{
                marginTop: '24px',
                padding: '10px 24px',
                background: '#3b82f6',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: 600,
              }}
            >
              Got it!
            </button>
          </div>
        </div>
      )}

      {showAbout && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          zIndex: 1000,
          paddingRight: '20px',
        }}>
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
            background: 'white',
            borderRadius: '12px',
            padding: '32px',
            width: '400px',
            maxHeight: '80vh',
            overflow: 'auto',
            boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)',
          }}>
            <h2 style={{ marginTop: 0, marginBottom: '16px', fontSize: '24px', fontWeight: 700 }}>
              About This Model
            </h2>

            <div style={{
              padding: '12px',
              background: '#e0e7ff',
              border: '2px solid #6366f1',
              borderRadius: '8px',
              marginBottom: '16px',
              fontSize: '13px',
              lineHeight: '1.5'
            }}>
              <strong style={{ color: '#3730a3' }}>⚠️ Hybrid Modeling Approach:</strong>
              <div style={{ color: '#4338ca', marginTop: '4px' }}>
                This simulator intentionally combines <strong>Causal Loop Diagram notation</strong> (polarities, feedback loops)
                with <strong>numerical simulation</strong> (typical of Stock-and-Flow models). This hybrid approach prioritizes
                accessibility and stakeholder communication over strict Systems Dynamics formalism. It's appropriate for
                conceptual exploration but would require conversion to proper SFD methodology for rigorous policy analysis.
              </div>
            </div>

            <div style={{
              padding: '12px',
              background: '#fef3c7',
              border: '2px solid #f59e0b',
              borderRadius: '8px',
              marginBottom: '16px',
              fontSize: '13px',
              lineHeight: '1.5'
            }}>
              <strong style={{ color: '#92400e' }}>⚠️ Proof of Concept:</strong>
              <div style={{ color: '#78350f', marginTop: '4px' }}>
                This is a <strong>conceptual exploration tool</strong> with manually determined assumptions.
                A production version would use parameters derived from AI/ML analysis of historic data.
              </div>
            </div>

            <p style={{ fontSize: '14px', lineHeight: '1.6', marginBottom: '16px' }}>
              This simulator models a <strong>music cooperative platform</strong> that integrates streaming, physical manufacturing,
              distribution, recording, and performance infrastructure.
            </p>

            <h3 style={{ marginTop: '24px', marginBottom: '12px', fontSize: '16px', fontWeight: 600 }}>🎯 Purpose</h3>
            <p style={{ fontSize: '14px', lineHeight: '1.6', marginBottom: '16px' }}>
              To visualize and test how different variables interact and create reinforcing or balancing feedback loops
              that drive both economic sustainability and community value.
            </p>

            <h3 style={{ marginTop: '24px', marginBottom: '12px', fontSize: '16px', fontWeight: 600 }}>🎮 Simulation Modes</h3>
            <div style={{ fontSize: '14px', lineHeight: '1.6', marginBottom: '8px' }}>
              <strong>📊 Static Mode:</strong>
              <ul style={{ marginLeft: '20px', marginTop: '4px' }}>
                <li>Shows immediate cause-and-effect relationships</li>
                <li>No time progression or growth</li>
                <li>Good for understanding causal structure</li>
                <li>Use for: "What happens RIGHT NOW if I change X?"</li>
              </ul>
            </div>
            <div style={{ fontSize: '14px', lineHeight: '1.6', marginBottom: '16px' }}>
              <strong>📈 Predictive Mode:</strong>
              <ul style={{ marginLeft: '20px', marginTop: '4px' }}>
                <li>Simulates growth over time (weeks/months)</li>
                <li>Shows <em>possible</em> dynamics, not <em>likely</em> outcomes</li>
                <li>Growth rates are hypothetical assumptions</li>
                <li>Use for: Exploring "what-if" scenarios only</li>
                <li><strong>Do NOT use for financial forecasting</strong></li>
              </ul>
            </div>

            <h3 style={{ marginTop: '24px', marginBottom: '12px', fontSize: '16px', fontWeight: 600 }}>🔄 Feedback Loops</h3>
            <p style={{ fontSize: '14px', lineHeight: '1.6', marginBottom: '8px' }}>
              The model includes <strong>4 reinforcing loops (R1-R4)</strong> that drive growth and <strong>5 balancing loops (B1-B5)</strong> that create natural limits:
            </p>
            <div style={{ fontSize: '14px', lineHeight: '1.6', marginBottom: '8px' }}>
              <strong style={{ color: '#10b981' }}>↗️ Reinforcing Loops (Green):</strong>
              <ul style={{ marginLeft: '20px', marginTop: '4px' }}>
                <li><strong>R1:</strong> Revenue Growth - listeners drive exponential revenue</li>
                <li><strong>R2:</strong> Quality Reinforcement - quality attracts more listeners</li>
                <li><strong>R3:</strong> Community Network - artists strengthen each other</li>
                <li><strong>R4:</strong> B2B Growth - stores create sustainable partnerships</li>
              </ul>
            </div>
            <div style={{ fontSize: '14px', lineHeight: '1.6', marginBottom: '8px' }}>
              <strong style={{ color: '#ef4444' }}>⚖️ Balancing Loops (Red/Orange):</strong>
              <ul style={{ marginLeft: '20px', marginTop: '4px' }}>
                <li><strong>B1:</strong> Artist Churn - low income causes artists to leave</li>
                <li><strong>B2:</strong> Overhead Limits - more artists = more burden</li>
                <li><strong>B3:</strong> Community Fragmentation - growth dilutes cohesion</li>
                <li><strong>B4:</strong> Lead Time Drag - manufacturing delays frustrate artists</li>
                <li><strong>B5:</strong> Store Dilution - finite shelf space limits growth</li>
              </ul>
            </div>
            <p style={{ fontSize: '13px', color: '#6b7280', fontStyle: 'italic', marginTop: '8px' }}>
              Click any R or B label in the diagram to see the complete loop path and highlight involved variables.
            </p>

            <h3 style={{ marginTop: '24px', marginBottom: '12px', fontSize: '16px', fontWeight: 600 }}>Visual Design</h3>
            <p style={{ marginBottom: '8px', fontSize: '14px', lineHeight: '1.6' }}>
              <strong>Node colors</strong> represent different system components. <strong>Edge styles</strong> convey relationship types:
            </p>
            <ul style={{ marginLeft: '20px', fontSize: '14px', lineHeight: '1.6', marginBottom: '8px' }}>
              <li><strong>Positive (+)</strong> relationships: Standard thickness, normal dash patterns</li>
              <li><strong>Negative (-)</strong> relationships: Thicker edges, 10-5 dash pattern, red labels</li>
            </ul>
            <p style={{ marginBottom: '8px', fontSize: '14px', lineHeight: '1.6' }}>
              <strong>Animated arrows</strong> show active flows with different speeds reflecting real-world timescales:
            </p>
            <ul style={{ marginLeft: '20px', fontSize: '14px', lineHeight: '1.6', marginBottom: '12px' }}>
              <li><strong>Money:</strong> Fast (subscriptions, revenue, fees)</li>
              <li><strong>Creation:</strong> Slow (artistic development, quality improvement)</li>
              <li><strong>Influence:</strong> Medium (reputation building, attraction)</li>
              <li><strong>Support:</strong> Medium (infrastructure, community, services)</li>
            </ul>

            <h3 style={{ marginTop: '24px', marginBottom: '12px', fontSize: '16px', fontWeight: 600 }}>⚠️ Current Limitations</h3>
            <ul style={{ marginLeft: '20px', fontSize: '14px', lineHeight: '1.8' }}>
              <li><strong>No historic data:</strong> All parameters are manual assumptions</li>
              <li><strong>No validation:</strong> Growth rates and thresholds are educated guesses</li>
              <li>Simplified model - real cooperatives are more complex</li>
              <li>No random events or external shocks</li>
              <li>Linear relationships (real effects may be non-linear)</li>
              <li>No governance or decision-making processes modeled</li>
            </ul>

            <h3 style={{ marginTop: '24px', marginBottom: '12px', fontSize: '16px', fontWeight: 600 }}>🚀 Production Version Requirements</h3>
            <p style={{ fontSize: '14px', lineHeight: '1.6', marginBottom: '8px' }}>
              A production-ready version would require:
            </p>
            <ul style={{ marginLeft: '20px', fontSize: '14px', lineHeight: '1.8' }}>
              <li><strong>Historic data collection</strong> from similar platforms (Bandcamp, Patreon, SoundCloud)</li>
              <li><strong>Machine learning calibration</strong> using regression, time-series analysis, and causal inference</li>
              <li><strong>Real co-op partnerships</strong> to gather live data and validate predictions</li>
              <li><strong>Confidence intervals</strong> on all predictions to quantify uncertainty</li>
              <li><strong>Continuous model updating</strong> as new data becomes available</li>
              <li><strong>A/B testing</strong> to validate cause-and-effect relationships</li>
            </ul>
            <p style={{ fontSize: '14px', lineHeight: '1.6', marginTop: '12px', fontStyle: 'italic', color: '#6b7280' }}>
              See the DATA_CALIBRATION.md file for detailed roadmap and methodology.
            </p>

            <button
              onClick={() => setShowAbout(false)}
              style={{
                marginTop: '24px',
                padding: '10px 24px',
                background: '#8b5cf6',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: 600,
              }}
            >
              Close
            </button>
          </div>
        </div>
      )}

{showNodeInfo && (
  <div style={{
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0,0,0,0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    zIndex: 1000,
    paddingRight: '20px',
  }}>
    <div
      onClick={(e) => e.stopPropagation()}
      style={{
      background: 'white',
      borderRadius: '12px',
      padding: '32px',
      width: '400px',
      boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)',
    }}>
      <h2 style={{ marginTop: 0, marginBottom: '16px', fontSize: '24px', fontWeight: 700 }}>
        {showNodeInfo.data.name}
      </h2>
      <div style={{ marginBottom: '16px' }}>
        <label style={{ display: 'block', fontSize: '14px', fontWeight: 600, marginBottom: '8px' }}>
          Current Value:
        </label>
        <input
          type="text"
          defaultValue={showNodeInfo.data.displayValue}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              const numValue = parseFloat(e.target.value.replace(/[^0-9.-]/g, ''));
              if (!isNaN(numValue)) {
                updateNodeValue(showNodeInfo.id, numValue);
                setShowNodeInfo(null);
              }
            }
          }}
          onBlur={(e) => {
            const numValue = parseFloat(e.target.value.replace(/[^0-9.-]/g, ''));
            if (!isNaN(numValue)) {
              updateNodeValue(showNodeInfo.id, numValue);
            }
          }}
          style={{
            width: '100%',
            padding: '10px',
            fontSize: '18px',
            fontWeight: 600,
            border: '2px solid #3b82f6',
            borderRadius: '6px',
          }}
          autoFocus
        />
        <p style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
          Press Enter or click outside to save
        </p>
      </div>
      <p style={{ fontSize: '14px', lineHeight: '1.6', marginBottom: '16px' }}>
        {nodeDescriptions[showNodeInfo.id]}
      </p>
      <button
        onClick={() => setShowNodeInfo(null)}
        style={{
          padding: '10px 24px',
          background: '#3b82f6',
          color: 'white',
          border: 'none',
          borderRadius: '6px',
          cursor: 'pointer',
          fontSize: '14px',
          fontWeight: 600,
        }}
      >
        Close
      </button>
    </div>
  </div>
)}

      {showEdgeInfo && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          zIndex: 1000,
          paddingRight: '20px',
        }}>
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
            background: 'white',
            borderRadius: '12px',
            padding: '32px',
            width: '400px',
            boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)',
          }}>
            <h2 style={{ marginTop: 0, marginBottom: '16px', fontSize: '24px', fontWeight: 700 }}>
              {showEdgeInfo.label}
            </h2>
            <p style={{ fontSize: '14px', lineHeight: '1.6', marginBottom: '16px' }}>
              {edgeDescriptions[showEdgeInfo.id]}
            </p>
            {showEdgeInfo.data?.flowType && (
              <div style={{
                padding: '12px',
                background: '#f3f4f6',
                borderRadius: '6px',
                fontSize: '13px',
                marginBottom: '16px'
              }}>
                <strong>Flow Type:</strong> {showEdgeInfo.data.flowType}
              </div>
            )}
            <button
              onClick={() => setShowEdgeInfo(null)}
              style={{
                padding: '10px 24px',
                background: '#3b82f6',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: 600,
              }}
            >
              Close
            </button>
          </div>
        </div>
      )}

      {showLoopInfo && loopDescriptions[showLoopInfo.id] && (
        <div
          style={{
          position: 'fixed',
          top: '80px',
          right: '20px',
          width: '380px',
          background: 'white',
          borderRadius: '12px',
          padding: '24px',
          boxShadow: '0 20px 25px -5px rgba(0,0,0,0.3)',
          zIndex: 999,
          maxHeight: 'calc(100vh - 100px)',
          overflowY: 'auto',
          border: `3px solid ${loopMembership[showLoopInfo.id]?.color}`,
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
            <h2 style={{ margin: 0, fontSize: '20px', fontWeight: 700, flex: 1 }}>
              {loopDescriptions[showLoopInfo.id].name}
            </h2>
            <button
              onClick={() => {
                setHighlightedLoop(null);
                setShowLoopInfo(null);
              }}
              style={{
                background: 'none',
                border: 'none',
                fontSize: '24px',
                cursor: 'pointer',
                padding: '0',
                marginLeft: '8px',
                color: '#6b7280',
                lineHeight: '1',
              }}
            >
              ×
            </button>
          </div>
          <div style={{
            display: 'inline-block',
            padding: '4px 12px',
            background: `${loopMembership[showLoopInfo.id]?.color}20`,
            border: `2px solid ${loopMembership[showLoopInfo.id]?.color}`,
            borderRadius: '6px',
            fontSize: '11px',
            fontWeight: 600,
            color: loopMembership[showLoopInfo.id]?.color,
            marginBottom: '12px'
          }}>
            {loopDescriptions[showLoopInfo.id].type === 'Reinforcing' ? '↗️ Reinforcing Loop' : '⚖️ Balancing Loop'}
          </div>
          <p style={{ fontSize: '13px', lineHeight: '1.6', marginBottom: '12px', color: '#374151' }}>
            {loopDescriptions[showLoopInfo.id].description}
          </p>
          <div style={{
            padding: '10px',
            background: '#f3f4f6',
            borderRadius: '6px',
            fontSize: '12px',
            fontFamily: 'monospace',
            lineHeight: '1.5'
          }}>
            <strong>Loop Path:</strong><br />
            {loopDescriptions[showLoopInfo.id].path}
          </div>
        </div>
      )}
    </div>
  );
}